import type { ApiChat, ApiUpdateChat } from '../../../api/types';
import type { ActionReturnType } from '../../types';
import { MAIN_THREAD_ID } from '../../../api/types';

import { ARCHIVED_FOLDER_ID, MAX_ACTIVE_PINNED_CHATS, SERVICE_NOTIFICATIONS_USER_ID } from '../../../config';
import { omit } from '../../../util/iteratees';
import { isLocalMessageId } from '../../../util/keys/messageKey';
import { closeMessageNotifications, notifyAboutMessage } from '../../../util/notifications';
import { debounce } from '../../../util/schedulers';
import { checkIfHasUnreadReactions } from '../../helpers';
import {
  addActionHandler, getGlobal, setGlobal,
} from '../../index';
import {
  addChatListIds,
  addUnreadMentions,
  deletePeerPhoto,
  leaveChat,
  removeUnreadMentions,
  replaceChatMessages,
  replacePeerPhotos,
  replacePinnedTopicIds,
  stampChatDiscussionRoots,
  updateChat,
  updateChatFullInfo,
  updateChatFullInfoMembers,
  updateChatListType,
  updateChatParticipantRank,
  updatePeerStoriesHidden,
  updateTopic,
} from '../../reducers';
import { removeUnreadReactions } from '../../reducers/reactions';
import { updateTabState } from '../../reducers/tabs';
import {
  addUnreadMessageToCounter,
  replaceTabThreadParam,
  replaceThreadDraftFromServer,
  replaceThreadLocalStateParam,
  replaceThreadReadStateParam,
  updateThreadReadState,
} from '../../reducers/threads';
import {
  selectChat,
  selectChatByClientOrRawId,
  selectChatFullInfo,
  selectChatListType,
  selectCommonBoxChatId,
  selectCurrentMessageList,
  selectIsChatListed,
  selectIsMessageInCurrentMessageList,
  selectIsViewportNewest,
  selectPeer,
  selectTabState,
} from '../../selectors';
import {
  selectThreadIdFromMessage,
  selectThreadLocalStateParam,
  selectThreadReadState,
} from '../../selectors/threads';

import { isBackgroundModeActive } from '../../../hooks/window/useBackgroundMode';

const TYPING_STATUS_CLEAR_DELAY = 6000; // 6 seconds
// Reordering folders on another device emits one `updateDialogFilters` per moved folder; re-read the
// list once for the burst. Runs on the leading edge so a single change still applies immediately.
const reloadChatFoldersDebounced = debounce((load: NoneToVoidFunction) => load(), 300, true);
const INVALIDATE_FULL_CHAT_FIELDS = new Set<keyof ApiChat>([
  'boostLevel', 'isForum', 'isLinkedInDiscussion', 'fakeType', 'restrictionReasons', 'isJoinToSend', 'isJoinRequest',
  'type',
]);

addActionHandler('apiUpdate', (global, actions, update): ActionReturnType => {
  switch (update['@type']) {
    case 'updateChat': {
      // The admin CMS backend emits group settings/permission changes as an updateChat carrying the
      // SERVER chat id (positive) at the top level, while the client keys chats by the negative id in
      // `chat.id` — plus a spurious `isNotJoined: true`. Left as-is the update lands on a phantom chat
      // and never applies live (only after a reload). When the two ids disagree, re-key to the client
      // id and drop the bogus flag. Inert once the backend emits a consistent id (ids then match).
      if (update.chat?.id !== undefined && String(update.chat.id) !== String(update.id)) {
        update = { ...update, id: update.chat.id };
      }
      // `isNotJoined` is a client-owned flag: it is set by our own leave/delete paths and cleared by
      // the chat list, which only ever lists chats the user is in. The backend nevertheless stamps it
      // on `updateChat` broadcasts (originally only on CMS ones, where the envelope id disagreed with
      // `chat.id` — now on matching-id ones too), and a single bogus `true` evicts the chat from the
      // list: it drops out of every folder, its pin disappears from the pinned block (shifting the
      // pins below it up), and the flag survives in the cache, so reloading cannot bring it back —
      // only clearing the cache does. Never take this flag from a chat payload.
      if (update.chat && 'isNotJoined' in update.chat) {
        update = { ...update, chat: omit(update.chat, ['isNotJoined']) };
      }
      const localChat = selectChat(global, update.id);
      const localReadState = selectThreadReadState(global, update.id, MAIN_THREAD_ID);
      const { isForum: prevIsForum } = localChat || {};
      const { lastReadOutboxMessageId } = localReadState || {};
      if (update.readState?.lastReadOutboxMessageId && lastReadOutboxMessageId
        && update.readState.lastReadOutboxMessageId < lastReadOutboxMessageId) {
        update = {
          ...update,
          readState: omit(update.readState, ['lastReadOutboxMessageId']),
        };
      }
      global = updateChat(global, update.id, update.chat);

      // A chat appearing for the first time (first message from someone new) has to be ADDED to the
      // list, not merely merged into `byId`: the chat list and every folder are built from
      // `listIds`, so a chat missing from it renders nowhere and counts toward no folder's unread
      // badge until a full reload repopulates the list.
      if (update.isNew && !selectIsChatListed(global, update.id)) {
        const listType = update.chat.folderId === ARCHIVED_FOLDER_ID ? 'archived' : 'active';
        global = addChatListIds(global, listType, [update.id]);
      }

      if (update.readState) {
        global = updateThreadReadState(global, update.id, MAIN_THREAD_ID, update.readState);
      }

      if (localChat?.areStoriesHidden !== update.chat.areStoriesHidden) {
        global = updatePeerStoriesHidden(global, update.id, update.chat.areStoriesHidden || false);
      }

      const localAdminRights = localChat?.adminRights;
      const newAdminRights = update.chat.adminRights;

      if (localAdminRights && localAdminRights.manageDirectMessages && !update.chat.isMin
        && newAdminRights?.manageDirectMessages !== localAdminRights.manageDirectMessages
        && localChat.linkedMonoforumId) {
        global = replaceChatMessages(global, localChat.linkedMonoforumId, {});
      }

      setGlobal(global);

      // A chat missing from the list is refetched even when it carries `isNotJoined`. The flag used to
      // gate this refresh, which made it self-sustaining: a chat wrongly flagged was hidden from the
      // sidebar and, because the flag also suppressed the one request that would have re-listed it and
      // cleared it, stayed hidden until the app was restarted. The chat list is the authority — ask it.
      if (!update.noTopChatsRequest && !selectIsChatListed(global, update.id)) {
        // Reload top chats to update chat listing
        actions.loadTopChats();
      }

      if (update.chat.id) {
        closeMessageNotifications({
          chatId: update.chat.id,
          lastReadInboxMessageId: update.readState?.lastReadInboxMessageId,
        });
      }

      Object.values(global.byTabId).forEach(({ id: tabId }) => {
        const { chatId: currentChatId } = selectCurrentMessageList(global, tabId) || {};
        const chatUpdate = update as ApiUpdateChat;
        // The property `isForum` was changed in another client
        if (currentChatId === chatUpdate.id
          && 'isForum' in chatUpdate.chat && prevIsForum !== chatUpdate.chat.isForum) {
          if (prevIsForum) {
            actions.closeForumPanel({ tabId });
          }
          actions.openChat({ id: currentChatId, tabId });
        }
      });

      if (localChat) {
        const chatUpdate = update.chat;
        const changedFields = (Object.keys(chatUpdate) as (keyof ApiChat)[])
          .filter((key) => localChat[key] !== chatUpdate[key]);
        if (changedFields.some((key) => INVALIDATE_FULL_CHAT_FIELDS.has(key))) {
          actions.invalidateFullInfo({ peerId: update.id });
        }
      }

      return undefined;
    }

    case 'updateChatJoin': {
      const joinedChatId = update.id;
      const chat = selectChat(global, joinedChatId);

      // The chat may be unknown to this client — e.g. the user was just added to a freshly created
      // group. The update only carries the id, so refetch dialogs to materialize and list it.
      // `loadTopChats` merges any new chats returned by the backend into the list.
      if (!chat) {
        actions.loadTopChats();
        return undefined;
      }

      const listType = selectChatListType(global, joinedChatId);

      global = updateChat(global, joinedChatId, { isNotJoined: false, isForbidden: false });
      // While the user is not a member the backend answers the channel's history with an empty
      // result, and that empty `listedIds` reads as "loaded, nothing here" — the message list
      // renders the no-messages state instead of a loading one, so nothing ever asks for the
      // history again once membership lands. The blank view then outlives a reload, since the
      // empty thread state is cached. Drop the preview's ids so the reload below repopulates them.
      global = replaceThreadLocalStateParam(global, joinedChatId, MAIN_THREAD_ID, 'listedIds', undefined);
      Object.values(global.byTabId).forEach(({ id: tabId }) => {
        global = replaceTabThreadParam(global, joinedChatId, MAIN_THREAD_ID, 'viewportIds', undefined, tabId);
      });
      setGlobal(global);

      actions.requestChatUpdate({ chatId: chat.id });
      actions.loadFullChat({ chatId: joinedChatId, force: true });

      Object.values(global.byTabId).forEach(({ id: tabId }) => {
        const { chatId: currentChatId, threadId } = selectCurrentMessageList(global, tabId) || {};
        if (currentChatId !== joinedChatId || threadId !== MAIN_THREAD_ID) return;
        actions.loadViewportMessages({ chatId: joinedChatId, threadId: MAIN_THREAD_ID, tabId });
      });

      if (!listType) {
        // Known chat that isn't listed yet — refetch dialogs to place it in the list.
        actions.loadTopChats();
        return undefined;
      }

      global = getGlobal();
      global = addChatListIds(global, listType, [joinedChatId]);
      setGlobal(global);

      return undefined;
    }

    case 'updateChatLeave': {
      // The backend now names the leaver (and routes the update to them alone — everyone else hears
      // about the same leave through `updateChatMembers`), so trust the id when it is there: a leave
      // by anyone else cannot touch our list, and our own leave from another device takes the chat
      // out at once instead of waiting for a round trip. Ids arrive as numbers in some updates and as
      // strings in others, so compare as strings.
      const leaverId = update.userId !== undefined ? String(update.userId) : undefined;
      if (leaverId) {
        return leaverId === global.currentUserId ? leaveChat(global, String(update.id)) : undefined;
      }

      // Older backends send no leaver at all, which makes a broadcast about another member
      // indistinguishable from our own leave. Evicting on that took chats the user is still in out of
      // every folder, pin included, and the `isNotJoined` it set outlived the refetch, so they came
      // back only after clearing the cache. Reconcile instead: the chat list holds exactly the chats
      // the user is still in, so one we really left drops out and one we did not stays.
      actions.loadTopChats();

      return undefined;
    }

    case 'updateChatTypingStatus': {
      const {
        id, threadId = MAIN_THREAD_ID, typingStatus, peerId,
      } = update;
      const currentTypingStatusByPeerId = selectThreadLocalStateParam(global, id, threadId, 'typingStatusByPeerId');

      if (!typingStatus) {
        if (!currentTypingStatusByPeerId?.[peerId]) {
          return undefined;
        }

        const nextTypingStatusByPeerId = omit(currentTypingStatusByPeerId, [peerId]);
        global = replaceThreadLocalStateParam(
          global,
          id,
          threadId,
          'typingStatusByPeerId',
          Object.keys(nextTypingStatusByPeerId).length ? nextTypingStatusByPeerId : undefined,
        );
        setGlobal(global);

        return undefined;
      }

      const updatedTypingStatusByPeerId = currentTypingStatusByPeerId
        ? { ...currentTypingStatusByPeerId, [peerId]: typingStatus }
        : { [peerId]: typingStatus };
      global = replaceThreadLocalStateParam(global, id, threadId, 'typingStatusByPeerId', updatedTypingStatusByPeerId);
      setGlobal(global);

      setTimeout(() => {
        global = getGlobal();
        const actualTypingStatusByPeerId = selectThreadLocalStateParam(global, id, threadId, 'typingStatusByPeerId');
        const currentTypingStatus = actualTypingStatusByPeerId?.[peerId];

        if (!currentTypingStatus || typingStatus.timestamp !== currentTypingStatus.timestamp) {
          return;
        }

        const nextTypingStatusByPeerId = omit(actualTypingStatusByPeerId, [peerId]);
        global = replaceThreadLocalStateParam(
          global,
          id,
          threadId,
          'typingStatusByPeerId',
          Object.keys(nextTypingStatusByPeerId).length ? nextTypingStatusByPeerId : undefined,
        );
        setGlobal(global);
      }, TYPING_STATUS_CLEAR_DELAY);

      return undefined;
    }

    case 'newMessage': {
      const { id } = update;

      const isOur = update.message.senderId
        ? update.message.senderId === global.currentUserId : update.message.isOutgoing;
      if (isOur && !update.message.isFromScheduled) {
        return undefined;
      }

      const isLocal = isLocalMessageId(id);

      // Mirror `apiUpdaters/messages.ts`: the backend stamps a supergroup's realtime `chatId` in the
      // raw plain-negative form (`-137`) instead of the client channel form (`-1000000000137`), so a
      // direct `selectChat` misses and the unread counter (below) never increments — the sidebar/folder
      // badge stayed empty for those chats until a reload. Resolve by raw peer id and re-stamp so the
      // count targets the same chat the message renders into. No-op once the backend sends the client id.
      const chat = selectChatByClientOrRawId(global, update.chatId);
      if (!chat) {
        return undefined;
      }
      const chatId = chat.id;
      const message = chat.id !== update.message.chatId
        ? { ...update.message, chatId: chat.id }
        : update.message;

      // A delivered message means the sender stopped typing — clear their typing status right away
      // instead of waiting for the auto-clear timeout (`TYPING_STATUS_CLEAR_DELAY`).
      const { senderId } = message;
      if (senderId) {
        const typingStatusByPeerId = selectThreadLocalStateParam(
          global, chatId, MAIN_THREAD_ID, 'typingStatusByPeerId',
        );
        if (typingStatusByPeerId?.[senderId]) {
          const nextTypingStatusByPeerId = omit(typingStatusByPeerId, [senderId]);
          global = replaceThreadLocalStateParam(
            global,
            chatId,
            MAIN_THREAD_ID,
            'typingStatusByPeerId',
            Object.keys(nextTypingStatusByPeerId).length ? nextTypingStatusByPeerId : undefined,
          );
        }
      }

      const hasMention = Boolean(message.hasUnreadMention);

      const threadId = selectThreadIdFromMessage(global, message) || MAIN_THREAD_ID;
      // When some tab has this chat open at the newest viewport, the arriving message is effectively
      // already seen. Counting it as unread would leave a phantom sidebar badge that only clears after
      // a manual scroll to the bottom (the decrement path — the viewport read observer — can miss the
      // just-arrived message). Any tab may be the one viewing it, so check them all rather than the
      // current one. Treat it as read on arrival instead.
      // An unfocused window is nobody's viewport though: with the same account open on another device
      // (phone), reading here marks messages the user never saw, and their unread badge over there
      // appears and vanishes a moment later. The viewport read observer freezes in background for the
      // same reason (`useMessageObservers`) and replays its entries on refocus, which reads them then.
      const isViewable = !isLocal && !isBackgroundModeActive();
      const viewingTabId = !isViewable ? undefined : Object.values(global.byTabId).find(
        ({ id: candidateTabId }) => selectIsMessageInCurrentMessageList(global, chatId, message, candidateTabId)
          && selectIsViewportNewest(global, chatId, threadId, candidateTabId),
      )?.id;
      const isActivelyViewed = viewingTabId !== undefined;

      if ((!isLocal || chat.id === SERVICE_NOTIFICATIONS_USER_ID) && !isActivelyViewed) {
        global = addUnreadMessageToCounter(global, chatId, message);

        if (hasMention) {
          global = addUnreadMentions({
            global,
            chatId,
            ids: [id],
          });
        }
      }

      if (isActivelyViewed) {
        // Keep the read state consistent (so `selectFirstUnreadId` doesn't flag it) and sync the read
        // position to the backend so the count doesn't resurrect on reload.
        global = replaceThreadReadStateParam(global, chatId, threadId, 'lastReadInboxMessageId', id);
      }

      setGlobal(global);

      if (viewingTabId !== undefined) {
        actions.markMessageListRead({ maxId: id, tabId: viewingTabId });
      }

      notifyAboutMessage({
        chat,
        message,
      });

      return undefined;
    }

    case 'updateCommonBoxMessages':
    case 'updateChannelMessages': {
      const { ids, messageUpdate } = update;

      ids.forEach((id) => {
        const chatId = ('channelId' in update ? update.channelId : selectCommonBoxChatId(global, id))!;

        if (messageUpdate.reactions && !checkIfHasUnreadReactions(global, messageUpdate.reactions)) {
          global = removeUnreadReactions({ global, chatId, ids: [id] });
        }

        if (!messageUpdate.hasUnreadMention) {
          global = removeUnreadMentions({
            global,
            chatId,
            ids: [id],
          });
        }
      });

      return global;
    }

    case 'updateChatFullInfo': {
      global = updateChatFullInfo(global, update.id, update.fullInfo);
      // Linking a discussion group is what turns its stored history into channel-post copies, so
      // re-stamp as soon as the link lands (see `stampChatDiscussionRoots`).
      if (update.fullInfo.linkedChatId) {
        global = stampChatDiscussionRoots(global, update.id);
      }

      return global;
    }

    case 'updatePinnedChatIds': {
      const { ids, folderId } = update;
      const listType = folderId === ARCHIVED_FOLDER_ID ? 'archived' : 'active';
      if (!ids) {
        actions.loadPinnedDialogs({ listType });
        return global;
      }

      return {
        ...global,
        chats: {
          ...global.chats,
          orderedPinnedIds: {
            ...global.chats.orderedPinnedIds,
            [listType]: ids.length ? ids : undefined,
          },
        },
      };
    }

    case 'updatePinnedSavedDialogIds': {
      const { ids } = update;

      return {
        ...global,
        chats: {
          ...global.chats,
          orderedPinnedIds: {
            ...global.chats.orderedPinnedIds,
            saved: ids.length ? ids : undefined,
          },
        },
      };
    }

    case 'updateChatPinned': {
      const { id, isPinned } = update;
      const listType = selectChatListType(global, id);
      if (!listType) {
        return undefined;
      }

      const { [listType]: orderedPinnedIds } = global.chats.orderedPinnedIds;

      let newOrderedPinnedIds = orderedPinnedIds || [];
      if (!isPinned) {
        newOrderedPinnedIds = newOrderedPinnedIds.filter((pinnedId) => pinnedId !== id);
      } else if (!newOrderedPinnedIds.includes(id)) {
        // When moving pinned chats to archive, active ordered pinned ids don't get updated
        // (to preserve chat pinned state when it returns from archive)
        // If user already has max pinned chats, we should check for orderedIds
        // that don't point to listed chats
        if (listType === 'active' && newOrderedPinnedIds.length >= MAX_ACTIVE_PINNED_CHATS) {
          const listIds = global.chats.listIds.active;
          newOrderedPinnedIds = newOrderedPinnedIds.filter((pinnedId) => listIds && listIds.includes(pinnedId));
        }

        newOrderedPinnedIds = [id, ...newOrderedPinnedIds];
      }

      return {
        ...global,
        chats: {
          ...global.chats,
          orderedPinnedIds: {
            ...global.chats.orderedPinnedIds,
            [listType]: newOrderedPinnedIds.length ? newOrderedPinnedIds : undefined,
          },
        },
      };
    }

    case 'updateSavedDialogPinned': {
      const { id, isPinned } = update;

      const { saved: orderedPinnedIds } = global.chats.orderedPinnedIds;

      let newOrderedPinnedIds = orderedPinnedIds || [];
      if (!isPinned) {
        newOrderedPinnedIds = newOrderedPinnedIds.filter((pinnedId) => pinnedId !== id);
      } else if (!newOrderedPinnedIds.includes(id)) {
        newOrderedPinnedIds = [id, ...newOrderedPinnedIds];
      }

      return {
        ...global,
        chats: {
          ...global.chats,
          orderedPinnedIds: {
            ...global.chats.orderedPinnedIds,
            saved: newOrderedPinnedIds.length ? newOrderedPinnedIds : undefined,
          },
        },
      };
    }

    case 'updateChatListType': {
      const { id, folderId } = update;

      return updateChatListType(global, id, folderId);
    }

    case 'updateChatFolder': {
      const { id, folder } = update;
      const { byId: chatFoldersById, orderedIds } = global.chatFolders;

      const isDeleted = folder === undefined;

      Object.values(global.byTabId).forEach(({ id: tabId }) => {
        const tabState = selectTabState(global, tabId);
        const isFolderActive = Object.values(chatFoldersById)[tabState.activeChatFolder - 1]?.id === id;

        if (isFolderActive) {
          global = updateTabState(global, { activeChatFolder: 0 }, tabId);
        }
      });

      const newChatFoldersById = !isDeleted ? { ...chatFoldersById, [id]: folder } : omit(chatFoldersById, [id]);
      const newOrderedIds = !isDeleted
        ? orderedIds?.includes(id) ? orderedIds : [...(orderedIds || []), id]
        : orderedIds?.filter((orderedId) => orderedId !== id);

      return {
        ...global,
        chatFolders: {
          ...global.chatFolders,
          byId: newChatFoldersById,
          orderedIds: newOrderedIds,
          invites: omit(global.chatFolders.invites, [id]),
        },
      };
    }

    case 'updateChatFoldersOrder': {
      const { orderedIds } = update;

      return {
        ...global,
        chatFolders: {
          ...global.chatFolders,
          orderedIds,
        },
      };
    }

    case 'updateDialogFilters': {
      // The only thing the backend says when folders change on another device is "they changed" —
      // no payload — so re-read the list. Nothing handled this update at all, which is why a folder
      // created, renamed or deleted on the phone stayed as it was on the web until a reload.
      // Debounced because reordering emits one of these per moved folder.
      reloadChatFoldersDebounced(actions.loadChatFolders);
      return undefined;
    }

    case 'updateRecommendedChatFolders': {
      const { folders } = update;

      return {
        ...global,
        chatFolders: {
          ...global.chatFolders,
          recommended: folders,
        },
      };
    }

    case 'updateChatMembers': {
      // The backend serialises ids in this update inconsistently — sometimes as raw numbers (e.g.
      // `deletedMemberId: 38`), sometimes as strings (`id: '-140'`) — whereas the rest of the app
      // keys chats and users by string ids. Coerce so chat lookups and member comparisons match.
      const chatId = String(update.id);
      const { replacedMembers, addedMember } = update;
      const deletedMemberId = update.deletedMemberId !== undefined ? String(update.deletedMemberId) : undefined;
      const addedMemberId = addedMember ? String(addedMember.userId) : undefined;
      const isSelfAdded = Boolean(addedMemberId && addedMemberId === global.currentUserId);
      const isSelfRemoved = Boolean(deletedMemberId && deletedMemberId === global.currentUserId);

      const chat = selectChat(global, chatId);
      if (!chat) {
        // We were just added to a chat this client doesn't have yet (e.g. a freshly created group).
        // Refetch dialogs so it shows up in the list.
        if (isSelfAdded) {
          actions.loadTopChats();
        }
        return undefined;
      }

      if (isSelfRemoved) {
        // The current user was removed/kicked. Mirror `updateChatLeave` so the chat leaves the list
        // even if the backend routes self-removal through this update instead of `updateChatLeave`.
        return leaveChat(global, chatId);
      }

      if (isSelfAdded && (chat.isNotJoined || !selectIsChatListed(global, chatId))) {
        // The current user was just added: re-added after leaving, or approved after a join request.
        // Both hide the chat, but differently — leaving drops it from `listIds` (`updateChatLeave` /
        // `deleteHistory`) while a pending join request lists it with `isNotJoined` set, which keeps
        // it out of every folder. So clear the flag AND re-list, then refresh its full info;
        // handling only one of the two left an approved requester staring at an unchanged chat list
        // until a reload refetched the dialogs.
        const listType = chat.folderId === ARCHIVED_FOLDER_ID ? 'archived' : 'active';
        global = updateChat(global, chatId, {
          isNotJoined: false,
          isForbidden: false,
          currentUserBannedRights: undefined,
          adminRights: undefined,
        });
        global = addChatListIds(global, listType, [chatId]);
        setGlobal(global);

        actions.loadFullChat({ chatId, force: true });
        return undefined;
      }

      const currentMembers = selectChatFullInfo(global, chatId)?.members;
      const nextMembers = currentMembers ? [...currentMembers] : undefined;
      let nextMembersCount = chat.membersCount;
      let shouldUpdateMembers = false;

      if (replacedMembers) {
        // Authoritative full list — recompute the count from it.
        global = updateChatFullInfoMembers(global, chatId, replacedMembers);
        global = updateChat(global, chatId, { membersCount: replacedMembers.length });
        return global;
      }

      if (addedMember) {
        const isAlreadyMember = nextMembers?.some((m) => String(m.userId) === addedMemberId);
        if (nextMembers && !isAlreadyMember) {
          nextMembers.push(addedMember);
          shouldUpdateMembers = true;
        }
        // Bump the count when we actually added, or when the members list isn't loaded on this
        // client. Skip when the member is already in a loaded list to avoid double-counting.
        if (shouldUpdateMembers || !nextMembers) {
          nextMembersCount = (nextMembersCount ?? 0) + 1;
        }
      } else if (deletedMemberId) {
        const deleteIndex = nextMembers?.findIndex((m) => String(m.userId) === deletedMemberId) ?? -1;
        if (nextMembers && deleteIndex > -1) {
          nextMembers.splice(deleteIndex, 1);
          shouldUpdateMembers = true;
        }
        // Decrement the count when we actually removed, or when the members list isn't loaded (so
        // the header and profile subtitle update for clients that never opened the chat). Skip when
        // the member is missing from a loaded list (e.g. already removed optimistically on the
        // acting tab) to avoid double-counting.
        if ((shouldUpdateMembers || !nextMembers) && nextMembersCount !== undefined) {
          nextMembersCount = Math.max(nextMembersCount - 1, 0);
        }
      }

      if (nextMembersCount !== chat.membersCount) {
        global = updateChat(global, chatId, { membersCount: nextMembersCount });
      }
      if (shouldUpdateMembers && nextMembers) {
        global = updateChatFullInfoMembers(global, chatId, nextMembers);
      }

      return global;
    }

    case 'updateChatParticipantRank': {
      return updateChatParticipantRank(global, update.id, update.userId, update.rank);
    }

    case 'draftMessage': {
      const {
        chatId, threadId, draft,
      } = update;
      const chat = global.chats.byId[chatId];
      if (!chat) {
        return undefined;
      }

      return replaceThreadDraftFromServer(global, chatId, threadId || MAIN_THREAD_ID, draft);
    }

    case 'updatePendingJoinRequests': {
      const { chatId, requestsPending, recentRequesterIds } = update;
      const chat = global.chats.byId[chatId];
      if (!chat) {
        return undefined;
      }

      global = updateChatFullInfo(global, chatId, {
        requestsPending,
        recentRequesterIds,
      });
      setGlobal(global);

      actions.loadChatJoinRequests({ chatId });
      return undefined;
    }

    // "Mark as unread" made on another device
    case 'updateChatUnreadMark': {
      const { chatId, isMarkedAsUnread } = update;

      return updateThreadReadState(global, chatId, MAIN_THREAD_ID, {
        hasUnreadMark: isMarkedAsUnread || undefined,
      });
    }

    case 'updatePinnedTopic': {
      const { chatId, topicId, isPinned } = update;

      const chat = global.chats.byId[chatId];
      if (!chat) {
        return undefined;
      }

      global = updateTopic(global, chatId, topicId, {
        isPinned,
      });
      setGlobal(global);

      return undefined;
    }

    case 'updatePinnedTopicsOrder': {
      const { chatId, order } = update;

      const chat = global.chats.byId[chatId];
      if (!chat) return undefined;

      global = replacePinnedTopicIds(global, chatId, order);
      setGlobal(global);

      return undefined;
    }

    case 'updateTopic': {
      const { chatId, topicId } = update;

      const chat = selectChat(global, chatId);
      if (!chat?.isForum) return undefined;

      actions.loadTopicById({ chatId, topicId });

      return undefined;
    }

    case 'updateTopics': {
      const { chatId } = update;

      const chat = selectChat(global, chatId);
      if (!chat?.isForum) return undefined;

      actions.loadTopics({ chatId, force: true });

      return undefined;
    }

    case 'updateDiscussion': {
      const { chatId, threadId } = update;

      actions.loadDiscussion({ chatId, threadId });

      return undefined;
    }

    case 'updateViewForumAsMessages': {
      const { chatId, isEnabled } = update;

      const chat = selectChat(global, chatId);
      if (!chat?.isForum) return undefined;

      global = updateChat(global, chatId, {
        isForumAsMessages: isEnabled,
      });
      setGlobal(global);
      break;
    }

    case 'updateNewProfilePhoto': {
      const { peerId, photo } = update;

      global = updateChat(global, peerId, {
        avatarPhotoId: photo.id,
      });
      setGlobal(global);

      actions.loadMoreProfilePhotos({ peerId, shouldInvalidateCache: true });

      break;
    }

    case 'updateDeleteProfilePhoto': {
      const { peerId, photoId } = update;

      const peer = selectPeer(global, peerId);
      if (!peer) {
        return undefined;
      }

      if (!photoId || peer.avatarPhotoId === photoId) {
        global = updateChat(global, peerId, {
          avatarPhotoId: undefined,
        });
        global = replacePeerPhotos(global, peerId, undefined);
      } else {
        global = deletePeerPhoto(global, peerId, photoId);
      }
      setGlobal(global);

      actions.loadMoreProfilePhotos({ peerId, shouldInvalidateCache: true });

      break;
    }
  }

  return undefined;
});
